// utils/assetLifecycle.js
//
// Asset Life / End Life display helpers, shared by
// pages/technicians/ProductUnitWisePage.js,
// pages/admins/inventroy/InventoryProductList.js and ProductHistoryModal.js.
//
// The backend stays the authority: services/productLifecycleService.js
// computes `expected_end_date` (End Life) and `lifecycle` for every product
// the API returns. End Life = Purchase Date + Asset Life, unless a Life
// Review set `expected_life_override_end_date`, which wins. The preview below
// repeats that rule only so a form can show the date while it is being
// filled in, before anything is saved.
import { dateOnlyParts } from "./dateOnly";

export const LIFE_UNITS = [
  { value: "YEARS", label: "Years" },
  { value: "MONTHS", label: "Months" },
];

// Display labels for the backend's existing LIFECYCLE codes -- the codes
// themselves are not changed or extended here.
export const LIFECYCLE_LABELS = {
  NOT_CONFIGURED: "Life Not Set",
  HEALTHY: "Healthy",
  EXPIRING_SOON: "Near End of Life",
  EXPECTED_LIFE_EXPIRED: "End of Life",
  EXTENDED_USE: "End of Life (In Use)",
  DISCARDED: "Discarded",
};

// Filter dropdown order (NOT_CONFIGURED / DISCARDED have their own filters).
export const LIFECYCLE_FILTER_CODES = ["HEALTHY", "EXPIRING_SOON", "EXPECTED_LIFE_EXPIRED", "EXTENDED_USE"];

export const lifecycleLabel = (code) => {
  const value = String(code || "NOT_CONFIGURED").toUpperCase();
  return LIFECYCLE_LABELS[value] || value;
};

// 'ok' | 'warn' | 'bad' | 'muted' -- each page maps it to its own badge class.
export const lifecycleTone = (code) =>
  ({
    HEALTHY: "ok",
    EXPIRING_SOON: "warn",
    EXPECTED_LIFE_EXPIRED: "bad",
    EXTENDED_USE: "warn",
    DISCARDED: "bad",
  }[String(code || "").toUpperCase()] || "muted");

export const isValidLifeValue = (v) => {
  if (v === "" || v === null || v === undefined) return true;
  const n = Number(v);
  return Number.isInteger(n) && n > 0;
};

const hasLifeValue = (v) => v !== "" && v !== null && v !== undefined;

/** 'YYYY-MM-DD' = purchaseDate + lifeValue lifeUnit, or null when not enough input. */
export function previewEndLifeDate(purchaseDate, lifeValue, lifeUnit) {
  const p = dateOnlyParts(purchaseDate);
  const n = Number(lifeValue);
  if (!p || !hasLifeValue(lifeValue) || !Number.isInteger(n) || n <= 0) return null;
  const months = String(lifeUnit || "YEARS").toUpperCase() === "MONTHS" ? n : n * 12;
  // Calendar-month addition in UTC, same as the backend's addToDateOnly
  // (29 Feb + 1 year rolls to 1 Mar there too -- both use JS Date rollover).
  const d = new Date(Date.UTC(p.y, p.mo - 1 + months, p.d));
  const pad = (x) => String(x).padStart(2, "0");
  return `${d.getUTCFullYear()}-${pad(d.getUTCMonth() + 1)}-${pad(d.getUTCDate())}`;
}

/**
 * What a form's End Life field shows while it is being filled in.
 * Returns { date: 'YYYY-MM-DD' | null, overridden: boolean, hint: string | null }.
 * A Life Review override wins, exactly as on the backend.
 */
export function endLifeFormState({ purchaseDate, lifeValue, lifeUnit, overrideEndDate }) {
  const override = dateOnlyParts(overrideEndDate) ? overrideEndDate : null;
  if (override) return { date: override, overridden: true, hint: null };

  const date = previewEndLifeDate(purchaseDate, lifeValue, lifeUnit);
  if (date) return { date, overridden: false, hint: null };

  const hasPurchase = !!dateOnlyParts(purchaseDate);
  const hasLife = hasLifeValue(lifeValue);
  let hint = "Set Purchase Date and Asset Life";
  if (hasLife && !hasPurchase) hint = "Set a Purchase Date to calculate";
  else if (hasPurchase && !hasLife) hint = "Set Asset Life to calculate";
  else if (hasPurchase && hasLife) hint = "Enter Asset Life as a whole number to calculate";
  return { date: null, overridden: false, hint };
}
